import { memo, useMemo, useState } from 'react';
import { SearchInput, Badge, Avatar } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { MOCK_PEOPLE } from '../../constants/people.js';
import { initialsFromName } from '../../utils/initials.js';

const FIELD_LABEL = 'mb-1.5 block text-[11.5px] font-semibold uppercase tracking-wide text-content-faint';

function matchesQuery(person, query) {
  if (!query) return true;
  const haystack = [person.name, person.role, person.department, ...(person.tags ?? [])]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

const AttestationPersonRow = memo(function AttestationPersonRow({
  person,
  selected,
  onSelect,
  oooLabel,
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(person.id)}
        className={[
          'flex w-full items-start gap-2.5 rounded-[5px] px-2.5 py-2 text-left transition-colors',
          selected
            ? 'border border-brand-500/40 bg-surface'
            : 'border border-transparent hover:bg-surface/80',
        ].join(' ')}
      >
        <Avatar
          size="lg"
          initials={person.initials ?? initialsFromName(person.name)}
          alt={person.name}
          className="bg-surface-muted text-[11px] font-semibold text-content"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13px] font-semibold text-content">{person.name}</span>
            {person.oooUntil ? (
              <Badge variant="warning" size="small">
                {oooLabel}
              </Badge>
            ) : null}
          </div>
          <p className="text-[11.5px] text-content-muted">
            {person.role} · {person.department}
          </p>
          {person.tags?.length ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {person.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-[3px] bg-content/10 px-1.5 py-0.5 text-[10px] text-content-muted"
                >
                  {tag}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </button>
    </li>
  );
});

/**
 * Searchable attestation directory (aims-htl `.atm-people`).
 * Inject `people` when the live org directory lands (CT-5094).
 */
function AttestationRecipientPicker({
  value,
  onChange,
  people = MOCK_PEOPLE,
}) {
  const { t } = useTranslation('work-queues-app');
  const [query, setQuery] = useState('');
  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(
    () => people.filter((person) => matchesQuery(person, normalizedQuery)),
    [people, normalizedQuery],
  );

  return (
    <div>
      <label className={FIELD_LABEL}>{t('attestation.modal.recipientLabel')}</label>
      <SearchInput
        value={query}
        onChange={(changeEvent) => setQuery(changeEvent.target.value)}
        onClear={() => setQuery('')}
        placeholder={t('attestation.modal.recipientSearch')}
        className="mb-2"
      />
      <div className="max-h-[220px] overflow-y-auto rounded-md border border-edge bg-surface-muted/40 p-1 scrollbar-aimsos-auto-sm">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-xs text-content-faint">{t('attestation.modal.noPeople')}</p>
        ) : (
          <ul className="flex flex-col gap-0.5">
            {filtered.map((person) => (
              <AttestationPersonRow
                key={person.id}
                person={person}
                selected={person.id === value}
                onSelect={onChange}
                oooLabel={t('attestation.modal.oooUntil', { date: person.oooUntil })}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default AttestationRecipientPicker;
